import React, { useState } from "react";

const UserTable = ({
  currentUsers,
  usersLength,
  totalPages,
  currentPage,
  handlePageChange,
  toggleDropdown,
  toggleModal,
  openDropdownId,
  handleEditModalOpen,
  handlePreviewModalOpen,
  handleDeleteModalOpen,
}) => {
  // State for tracking the search query
  const [searchQuery, setSearchQuery] = useState("");

  // Filter the users based on the search query (by email, phone number, or name)
  const filteredUsers = currentUsers.filter((user) =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.phone_number.includes(searchQuery) ||
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <section className="flex-grow bg-gray-50 dark:bg-gray-900 p-3 sm:p-5 antialiased">
      <div className="mx-auto max-w-screen-xl px-4 lg:px-12">
        <div className="bg-white dark:bg-gray-800 relative shadow-md sm:rounded-lg overflow-hidden">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 p-4">
            <div className="w-full md:w-1/2">
              {/* Search Input */}
              <form className="flex items-center">
                <label htmlFor="user-search" className="sr-only">
                  Search
                </label>
                <div className="relative w-full">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <i className="fa fa-search text-gray-500 dark:text-gray-400"></i>
                  </div>
                  <input
                    type="text"
                    id="user-search"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                    placeholder="Search by Email, Phone Number, or Name"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)} // Update search query on input change
                  />
                </div>
              </form>
            </div>
            <div className="flex flex-col md:flex-row items-center w-full md:w-auto justify-end space-y-2 md:space-y-0 md:space-x-3">
              <button
                type="button"
                className="flex items-center justify-center text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-primary-600 dark:hover:bg-primary-700 focus:outline-none dark:focus:ring-primary-800"
                onClick={toggleModal}
              >
                <i className="fa fa-plus h-3.5 w-3.5 mr-2"></i>
                Add User
              </button>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">User ID</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Email</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Name</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Phone Number</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-2 text-left text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user, index) => (
                    <tr key={index} className="border-b dark:border-gray-700">
                      <td className="px-4 py-3">{user.user_id}</td>
                      <td className="px-4 py-3">{user.email}</td>
                      <td className="px-4 py-3">{user.name}</td>
                      <td className="px-4 py-3">{user.phone_number}</td>
                      <td className="px-4 py-3">{user.account_status}</td>
                      <td className="px-6 py-12 flex items-center justify-center relative">
                        <button
                          id={`dropdown-button-${index}`}
                          className="inline-flex items-center justify-center text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 p-1.5 dark:hover:bg-gray-800 text-center text-gray-500 hover:text-gray-800 rounded-lg focus:outline-none dark:text-gray-400 dark:hover:text-gray-100"
                          type="button"
                          onClick={() => toggleDropdown(index)}
                        >
                          <i className="fa fa-ellipsis-h text-lg"></i>
                        </button>
                        <div
                          id={`dropdown-${index}`}
                          className={`${
                            openDropdownId === index ? "block" : "hidden"
                          } absolute left-0 top-20 w-25 bg-white rounded divide-y divide-gray-100 shadow dark:bg-gray-700 dark:divide-gray-600 z-50`}
                        >
                          <ul
                            className="py-1 text-sm"
                            aria-labelledby={`dropdown-button-${index}`}
                          >
                            <li>
                              <button
                                type="button"
                                className="flex items-center w-full py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg focus:outline-none"
                                onClick={() => handleEditModalOpen(user)}
                              >
                                <i className="fas fa-edit w-4 h-4 mr-2"></i>
                                Edit
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="flex items-center w-full py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg focus:outline-none"
                                onClick={() => handlePreviewModalOpen(user)}
                              >
                                <i className="fas fa-eye w-4 h-4 mr-2"></i>
                                Preview
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="flex items-center w-full py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-700 text-red-500 dark:hover:text-red-400 rounded-lg focus:outline-none"
                                onClick={() => handleDeleteModalOpen(user)}
                              >
                                <i className="fas fa-trash w-4 h-4 mr-2"></i>
                                Delete
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-3 text-center text-gray-500 dark:text-gray-400"
                    >
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination and Entries Info */}
            <div className="p-4 flex justify-between items-center border-t dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredUsers.length > 0 ? 1 : 0} to{" "}
                {Math.min(filteredUsers.length, currentUsers.length)} of {usersLength}{" "}
                Entries
              </span>
              <div className="flex space-x-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (pageNumber) => (
                    <button
                      key={pageNumber}
                      className={`px-3 py-1 rounded-lg ${
                        currentPage === pageNumber
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                      onClick={() => handlePageChange(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UserTable;
